import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * An expand-all / collapse-all broadcast to every section row of a document.
 * `tick` changes on every toggle so repeating the same `value` still fires.
 */
export type ExpandSignal = { value: boolean; tick: number };

/** The signal that broadcasts `value` after `prev`. */
export function nextExpandSignal(prev: ExpandSignal | null, value: boolean): ExpandSignal {
  return { value, tick: (prev?.tick ?? 0) + 1 };
}

/** A section row's own open state, overridden whenever a new signal arrives. */
export function useSectionOpen(
  defaultOpen: boolean,
  expandSignal?: ExpandSignal | null,
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (expandSignal != null) setOpen(expandSignal.value);
  }, [expandSignal]);
  return [open, setOpen];
}

/**
 * Expand-all / collapse-all state for a document's section rows: which way
 * the toggle points and the signal the rows listen to. `defaultExpanded`
 * must match the rows' own default so the toggle starts out pointing the
 * right way; a change of `resetKey` (e.g. the document id) starts over.
 */
export function useExpandAll({
  defaultExpanded = false,
  resetKey,
}: { defaultExpanded?: boolean; resetKey?: unknown } = {}) {
  const [allExpanded, setAllExpanded] = useState(defaultExpanded);
  const [expandSignal, setExpandSignal] = useState<ExpandSignal | null>(null);
  useEffect(() => {
    setAllExpanded(defaultExpanded);
    setExpandSignal(null);
  }, [resetKey, defaultExpanded]);
  const toggleAll = () => {
    const next = !allExpanded;
    setExpandSignal((prev) => nextExpandSignal(prev, next));
    setAllExpanded(next);
  };
  return { allExpanded, expandSignal, toggleAll };
}
